// Design-system showcase for BC-23 advanced agent formatting.
// Pattern mirrors Crisp: compact textarea by default, "Aa" toggle expands
// a Tiptap-shaped rich editor above the input row. Storage is HTML
// (sanitized server-side with Loofah). The bubble and email previews
// render the same HTML the editor emits.
//
// The real implementation uses Tiptap StarterKit + Link + Image. This
// mockup fakes the editor with contentEditable + execCommand so the design
// page renders without committing the codebase to the library yet.

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Avatar } from '@/components/ui/Avatar'
import { RichComposer } from '@/components/formatting/RichComposer'

const SAMPLE_HTML = `
<p>Hey Sarah — I dug into your account and here's what I found.</p>
<p>The duplicate profile is fixed now. A few notes for next time:</p>
<ul>
  <li><strong>Always</strong> log in from the same browser to keep your session</li>
  <li>If you need to switch devices, use the <em>magic link</em> in your email</li>
  <li>Run <code>pnpm dlx banshee-doctor</code> if anything looks off</li>
</ul>
<blockquote>Tip: we have a help article on this — see <a href="https://banshee.chat/docs/account">the docs</a></blockquote>
<p>Let me know if anything else is off!</p>
`.trim()

export function FormattingDemo() {
  const [sentHtml, setSentHtml] = useState(SAMPLE_HTML)

  return (
    <div className="space-y-12">
      <section>
        <h3 className="text-lg font-semibold mb-3">Composer — collapsed by default</h3>
        <p className="text-sm text-muted-foreground mb-4">
          The compact form is identical to today's <code>MessageComposer</code> (paperclip · emoji · textarea
          · send). A new <kbd>Aa</kbd> button in the action row toggles formatting — when on, the textarea is
          replaced with a Tiptap-shaped rich surface and a toolbar sits at its top. Action row stays put.
        </p>
        <div className="rounded-lg overflow-hidden bg-background">
          <RichComposer initialHtml={SAMPLE_HTML} onSend={setSentHtml} />
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Click <kbd>Aa</kbd> to expand. Select text and press a toolbar button to format. Click <kbd>Send</kbd>
          to pipe the editor's HTML through to the preview cards below.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">Rendered in the conversation</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Bubble renders the stored HTML (after server-side sanitization). The exact same markup goes to web,
          widget and the email mailer — no per-surface re-rendering.
        </p>
        <BubblePreview html={sentHtml} />
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">Rendered in the visitor's email</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Same HTML, dropped into the existing <code>agent_reply.html.erb</code> body cell. Premailer inlines
          styles for Gmail / Outlook. Inline GIFs render natively.
        </p>
        <EmailPreview html={sentHtml} />
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">Storage & sanitization</h3>
        <SyntaxSheet />
      </section>
    </div>
  )
}

const RICH_PROSE_CLASSES = [
  'prose prose-sm max-w-none dark:prose-invert',
  '[&_code]:bg-muted/50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[0.85em] [&_code]:font-mono',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-primary/50 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:opacity-90',
  '[&_a]:text-primary [&_a]:underline',
  '[&_img]:rounded-lg [&_img]:max-w-[260px]',
  '[&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:list-decimal [&_ol]:pl-5',
  '[&_p]:my-1'
].join(' ')

function BubblePreview({ html }: { html: string }) {
  return (
    <Card>
      <CardContent className="p-4 bg-muted/20 rounded-lg">
        <div className="flex gap-3 max-w-[80%] ml-auto flex-row-reverse">
          <Avatar name="Agent" avatarType="initials" size="sm" className="shrink-0" />
          <div className="flex flex-col gap-1">
            <div
              className={`rounded-2xl px-4 py-2 bg-primary text-primary-foreground text-sm ${RICH_PROSE_CLASSES}`}
              dangerouslySetInnerHTML={{ __html: html }}
            />
            <p className="text-xs text-muted-foreground text-right">Colin · 2m ago</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function EmailPreview({ html }: { html: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-normal text-muted-foreground">
          From: Banshee &lt;hello@in.banshee.chat&gt;
        </CardTitle>
        <CardDescription className="text-foreground">
          Re: You have a message from Old World Rankings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border border-border/60 p-5 bg-[#0f1219] text-[#E4E4E7] text-sm leading-relaxed">
          <p className="text-sm mb-3"><strong>Colin</strong> replied:</p>
          <div
            className={`border border-[#27272A] rounded-md p-4 bg-[#1C2130] ${RICH_PROSE_CLASSES}`}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <p className="text-xs text-muted-foreground mt-4">Reply to this email to continue the conversation.</p>
        </div>
      </CardContent>
    </Card>
  )
}

function SyntaxSheet() {
  const allowed = [
    'p', 'br', 'strong', 'em', 's', 'code', 'pre',
    'a (href, rel=nofollow)', 'ul', 'ol', 'li',
    'blockquote', 'img (src, alt — Tenor/uploads only)'
  ]
  const rejected = ['<script>', '<style>', 'onclick / onerror', 'iframe', 'object', 'embed', 'svg', 'form']
  return (
    <Card>
      <CardContent className="p-4 grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium mb-2">Pipeline</p>
          <ol className="list-decimal pl-5 space-y-1 text-sm">
            <li>Tiptap editor emits HTML on send (<code>editor.getHTML()</code>)</li>
            <li><code>messages.content</code> stores the HTML as <code>TEXT</code> — no schema change</li>
            <li>Server sanitizes with Loofah allowlist before broadcast / mail</li>
            <li>Web/widget render via <code>dangerouslySetInnerHTML</code> + DOMPurify safety net</li>
            <li>Mailer drops sanitized HTML straight into <code>agent_reply.html.erb</code></li>
            <li>iOS: <code>NSAttributedString(data:options:.html)</code> renderer (mobile follow-up)</li>
          </ol>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Allowed tags</p>
            <div className="flex flex-wrap gap-1.5">
              {allowed.map(t => <Badge key={t} variant="secondary" className="font-mono text-[10px]">{t}</Badge>)}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">Stripped by Loofah</p>
            <div className="flex flex-wrap gap-1.5">
              {rejected.map(t => <Badge key={t} variant="outline" className="font-mono text-[10px]">{t}</Badge>)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default FormattingDemo
